import { CONSTELLATION_SIZE } from '../constants';
import type { ConstellationDot, ConstellationFrame } from '../types';
import {
	getGlowSparkStrength,
	getRepeatingProgress,
	getSineWaveStrength,
	getStableVariation,
} from './poseHelpers';

type TwgDotInput = Readonly<{
	color: string;
	index: number;
	timeSeconds: number;
}>;

export type TwgTravelingPulse = Readonly<{
	strength: number;
	x: number;
	y: number;
}>;

type TwgPulseConfig = Readonly<{
	activeDurationSeconds: number;
	connectionSequenceOffset: number;
	phaseOffsetSeconds: number;
	restDurationSeconds: number;
}>;

const TWG_REFERENCE_SIZE = 56;
const GLIMMER_BASE_SPEED_RADIANS_PER_SECOND = 1.15;
const GLIMMER_SPEED_RANGE_RADIANS_PER_SECOND = 0.3;
const GLIMMER_PHASE_OFFSET_RADIANS = 2.41;
const TRANSMIT_PERIOD_SECONDS = 3.2;
const TRANSMIT_VARIATION_OFFSET = 5;
const TRANSMIT_FOCUS_POWER = 4;
const BASE_BRIGHTNESS = 0.4;
const GLIMMER_BRIGHTNESS = 0.42;
const TRANSMIT_BRIGHTNESS = 0.3;
const BASE_SCALE = 0.94;
const GLIMMER_SCALE = 0.1;
const TRANSMIT_SCALE = 0.16;
const SWAY_REFERENCE_PIXELS = 0.28;
const HORIZONTAL_SWAY_SPEED_RADIANS_PER_SECOND = 1.3;
const HORIZONTAL_SWAY_PHASE_OFFSET_RADIANS = 2.17;
const VERTICAL_SWAY_SPEED_RADIANS_PER_SECOND = 1.1;
const VERTICAL_SWAY_PHASE_OFFSET_RADIANS = 1.73;
const PULSE_CURVE_RADIANS = Math.PI;
const GLOW_CONCENTRATION_POWER = 1.5;

// Defines the connections in TWG
export const TWG_CONNECTIONS: ReadonlyArray<readonly [number, number]> = [
	[0, 1],
	[0, 2],
	[0, 3],
	[0, 4],
	[0, 5],
	[0, 6],
	[1, 7],
	[1, 8],
	[1, 2],
	[2, 9],
	[2, 10],
	[2, 3],
	[3, 11],
	[3, 12],
	[4, 13],
	[4, 14],
	[4, 5],
	[5, 15],
	[5, 16],
	[5, 6],
	[6, 17],
	[6, 18],
];

const TWG_PULSE_CONFIGS: readonly TwgPulseConfig[] = [
	{
		activeDurationSeconds: 1,
		connectionSequenceOffset: 41,
		phaseOffsetSeconds: 0.35,
		restDurationSeconds: 0.6,
	},
	{
		activeDurationSeconds: 0.8,
		connectionSequenceOffset: 73,
		phaseOffsetSeconds: 1.05,
		restDurationSeconds: 1,
	},
];

// Retains the irregular hub-and-spoke formation
const TWG_REFERENCE_POINTS: ReadonlyArray<readonly [number, number]> = [
	[29.7, 27.8],
	[39, 32.4],
	[27, 39.4],
	[17.2, 37.3],
	[15.8, 24.5],
	[27.9, 14.7],
	[38.6, 20.8],
	[49, 36.6],
	[47.2, 25.7],
	[26.2, 50],
	[36.6, 46],
	[19, 47.7],
	[9.5, 39],
	[11.7, 16.5],
	[6.3, 24],
	[26.4, 6.7],
	[16.8, 10.2],
	[45, 15.7],
	[39.5, 10.3],
];

const REFERENCE_TO_CONSTELLATION_SCALE = CONSTELLATION_SIZE / TWG_REFERENCE_SIZE;
const TWG_BASE_POSITIONS = TWG_REFERENCE_POINTS.map(([x, y]) => ({
	x: x * REFERENCE_TO_CONSTELLATION_SCALE,
	y: y * REFERENCE_TO_CONSTELLATION_SCALE,
}));

const getGlimmerStrength = (index: number, timeSeconds: number): number => {
	const glimmerSpeed =
		GLIMMER_BASE_SPEED_RADIANS_PER_SECOND +
		getStableVariation(index) * GLIMMER_SPEED_RANGE_RADIANS_PER_SECOND;

	return getSineWaveStrength(timeSeconds * glimmerSpeed + index * GLIMMER_PHASE_OFFSET_RADIANS);
};

const getTransmitStrength = (index: number, timeSeconds: number): number => {
	const cycleProgress = getRepeatingProgress(
		timeSeconds / TRANSMIT_PERIOD_SECONDS + getStableVariation(index + TRANSMIT_VARIATION_OFFSET),
	);

	// Lets each transmission swell and fade without a hard cycle reset.
	return Math.sin(cycleProgress * PULSE_CURVE_RADIANS) ** TRANSMIT_FOCUS_POWER;
};

const getSwayOffset = (index: number, timeSeconds: number): Readonly<{ x: number; y: number }> => {
	const swayDistance = SWAY_REFERENCE_PIXELS * REFERENCE_TO_CONSTELLATION_SCALE;

	return {
		x:
			Math.sin(
				timeSeconds * HORIZONTAL_SWAY_SPEED_RADIANS_PER_SECOND +
					index * HORIZONTAL_SWAY_PHASE_OFFSET_RADIANS,
			) * swayDistance,
		y:
			Math.cos(
				timeSeconds * VERTICAL_SWAY_SPEED_RADIANS_PER_SECOND +
					index * VERTICAL_SWAY_PHASE_OFFSET_RADIANS,
			) * swayDistance,
	};
};

export const createTwgDot = ({ color, index, timeSeconds }: TwgDotInput): ConstellationDot => {
	const basePosition = TWG_BASE_POSITIONS[index];
	const glimmerStrength = getGlimmerStrength(index, timeSeconds);
	const transmitStrength = getTransmitStrength(index, timeSeconds);
	const swayOffset = getSwayOffset(index, timeSeconds);

	return {
		brightness: Math.min(
			1,
			BASE_BRIGHTNESS +
				glimmerStrength * GLIMMER_BRIGHTNESS +
				transmitStrength * TRANSMIT_BRIGHTNESS,
		),
		color,
		glowStrength: getGlowSparkStrength(
			Math.max(glimmerStrength, transmitStrength),
			GLOW_CONCENTRATION_POWER,
		),
		scale: BASE_SCALE + glimmerStrength * GLIMMER_SCALE + transmitStrength * TRANSMIT_SCALE,
		x: basePosition.x + swayOffset.x,
		y: basePosition.y + swayOffset.y,
	};
};

const createTravelingPulse = (
	dots: ConstellationFrame,
	timeSeconds: number,
	config: TwgPulseConfig,
): TwgTravelingPulse | null => {
	const cycleDuration = config.activeDurationSeconds + config.restDurationSeconds;
	const elapsedSeconds = timeSeconds + config.phaseOffsetSeconds;
	const cycleIndex = Math.floor(elapsedSeconds / cycleDuration);
	const timeInCycle = elapsedSeconds % cycleDuration;

	if (timeInCycle > config.activeDurationSeconds) {
		return null;
	}

	const pulseProgress = timeInCycle / config.activeDurationSeconds;
	const travelProgress = 0.5 - 0.5 * Math.cos(pulseProgress * PULSE_CURVE_RADIANS);
	const connectionIndex = Math.floor(
		getStableVariation(cycleIndex + config.connectionSequenceOffset) * TWG_CONNECTIONS.length,
	);
	const [fromIndex, toIndex] = TWG_CONNECTIONS[connectionIndex];
	const from = dots[fromIndex];
	const to = dots[toIndex];

	return {
		strength: Math.sin(pulseProgress * PULSE_CURVE_RADIANS),
		x: from.x + (to.x - from.x) * travelProgress,
		y: from.y + (to.y - from.y) * travelProgress,
	};
};

export const getTwgTravelingPulses = (
	dots: ConstellationFrame,
	timeSeconds: number,
): readonly TwgTravelingPulse[] =>
	TWG_PULSE_CONFIGS.map((config) => createTravelingPulse(dots, timeSeconds, config)).filter(
		(pulse): pulse is TwgTravelingPulse => pulse !== null,
	);
